import {
  cloneData,
  DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData,
} from '../dto/dto.js';
import { MapUtils } from '../../utility/map-utils.js';
import { PaymentProvider } from './payment-provider.js';
import {
  fromEnumAsString, toEnumFromString,
} from '../../serialization/enum-utils.js';
import { VersionRange } from '../core/version-range.js';
import { PaymentAccountRef } from './payment-account-ref.js';

export class OnlineTransactionDetails extends DataModel {
  static readonly TYPE_ID: string = '41302b7228704752803425d7cf54c51e';
  static readonly SCHEMA_VERSION: number = 1;

  private constructor(
    public readonly provider: PaymentProvider,
    public readonly paymentRequestId: string,
    public readonly transactionId: string,
    public readonly invoiceGroupId: string,
    public readonly paymentId: string,
    public readonly checkoutSessionId: string,
    public readonly paymentAccount: PaymentAccountRef,
  ) {
    super();
  }

  get $typeId() {
    return OnlineTransactionDetails.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(OnlineTransactionDetails.SCHEMA_VERSION);
  }

  static create(options: {
    provider: PaymentProvider,
    paymentRequestId: string,
    transactionId: string,
    invoiceGroupId: string,
    paymentId: string,
    checkoutSessionId: string,
    paymentAccount: PaymentAccountRef,
  }): OnlineTransactionDetails {
    return new OnlineTransactionDetails(
        options.provider,
        options.paymentRequestId,
        options.transactionId,
        options.invoiceGroupId,
        options.paymentId,
        options.checkoutSessionId,
        options.paymentAccount,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): OnlineTransactionDetails {
    return OnlineTransactionDetails.create({
      provider: toEnumFromString(MapUtils.getString(map, 'provider'), PaymentProvider),
      paymentRequestId: MapUtils.getString(map, 'paymentRequestId'),
      transactionId: MapUtils.getString(map, 'transactionId'),
      invoiceGroupId: MapUtils.getString(map, 'invoiceGroupId'),
      paymentId: MapUtils.getString(map, 'paymentId'),
      checkoutSessionId: MapUtils.getString(map, 'checkoutSessionId'),
      paymentAccount: context.fromData<PaymentAccountRef>(MapUtils.getRecord(map, 'paymentAccount')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'provider': fromEnumAsString(this.provider),
      'paymentRequestId': this.paymentRequestId,
      'transactionId': this.transactionId,
      'invoiceGroupId': this.invoiceGroupId,
      'paymentId': this.paymentId,
      'checkoutSessionId': this.checkoutSessionId,
      'paymentAccount': context.toData(this.paymentAccount),
    };
  }

  clone(): this {
    return new OnlineTransactionDetails(
        this.provider,
        this.paymentRequestId,
        this.transactionId,
        this.invoiceGroupId,
        this.paymentId,
        this.checkoutSessionId,
        cloneData(this.paymentAccount),
    ) as this;
  }

  isSame(other: OnlineTransactionDetails): boolean {
    if(this === other) {
      return true;
    }

    return this.provider === other.provider &&
      this.paymentId === other.paymentId &&
      this.checkoutSessionId === other.checkoutSessionId &&
      this.paymentRequestId === other.paymentRequestId &&
      this.transactionId === other.transactionId &&
      this.invoiceGroupId === other.invoiceGroupId &&
      isSameData(other.paymentAccount, this.paymentAccount);
  }

  copyWith(data: Partial<OnlineTransactionDetails>): OnlineTransactionDetails {
    return new OnlineTransactionDetails(
        data.provider ?? this.provider,
        data.paymentRequestId ?? this.paymentRequestId,
        data.transactionId ?? this.transactionId,
        data.invoiceGroupId ?? this.invoiceGroupId,
        data.paymentId ?? this.paymentId,
        data.checkoutSessionId ?? this.checkoutSessionId,
        data.paymentAccount ?? cloneData(this.paymentAccount),
    );
  }
}

export class OnlineTransactionDetailsBuilder extends DataModelBuilder<OnlineTransactionDetails> {
  build(context: SerializationContext, map: SerializedData): OnlineTransactionDetails {
    return OnlineTransactionDetails.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(OnlineTransactionDetails.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OnlineTransactionDetails.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    const version = versionRange.to;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
